import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSiteData } from '../context/DataContext';
import { motion } from 'framer-motion';
import { FiSearch, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import ProductCard from './ProductCard/ProductCard';
import './Category/Category.css';
import CategorySlider from './CategorySlider/CategorySlider';
import CustomArtCard from './CustomArtCard/CustomArtCard';

const shuffleArray = (arr) => {
  return arr.slice().sort(() => 0.5 - Math.random());
};

const Shop = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialSearch = params.get('search') || '';
  const [search, setSearch] = useState(initialSearch);
  const [shuffledProducts, setShuffledProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 16;
  const navigate = useNavigate();
  const { products } = useSiteData();
  const searchInputRef = useRef(null);

  useEffect(() => {
    // Shuffle all products once on load
    if (products && products.length > 0) {
      setShuffledProducts(shuffleArray(products));
    }

    // Check if we should scroll to search (only if there's a search param)
    if (initialSearch && searchInputRef.current) {
      searchInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [initialSearch, products]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Arabic Normalization Helper
  const normalize = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .trim()
      .replace(/[أإآ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/[\u064B-\u0652]/g, ''); // Remove Arabic harakat
  };

  // Smart Search Logic with Synonyms and Team Associations
  const filteredProducts = shuffledProducts.filter(product => {
    if (!search.trim()) return true;

    const normalizedProductName = normalize(product.name);
    const normalizedCategoryId = normalize(product.categoryId);
    const searchTokens = search.toLowerCase().trim().split(/\s+/).map(t => normalize(t));

    const synonymMap = {
      // Players & Aliases
      'salah': ['mohamed', 'salah', 'صلاح', 'محمد'],
      'messi': ['leo', 'messi', 'ميسي', 'ليونيل'],
      'ronaldo': ['cristiano', 'ronaldo', 'cr7', 'رونالدو', 'كريستيانو'],
      'cr7': ['cristiano', 'ronaldo', 'cr7', 'رونالدو'],
      'mbappe': ['kylian', 'mbappe', 'مبابي', 'كيليان'],
      'haaland': ['erling', 'haaland', 'halland', 'هالاند'],
      'vini': ['vinicius', 'vini', 'فينيسيوس', 'فيني'],
      'yamal': ['lamine', 'yamal', 'يمال', 'لامين'],
      'bellingham': ['jude', 'bellingham', 'بيلينجهام', 'جود'],
      'palmer': ['cole', 'palmer', 'بالمر', 'كول'],
      'marmoush': ['omar', 'marmoush', 'مرموش', 'عمر'],
      'zidane': ['zinedine', 'zinelden', 'zidane', 'زيدان', 'زين الدين'],
      'ronaldinho': ['ronaldinho', 'رونالدينيو', 'ساحر'],

      // Teams -> Player associations
      'madrid': ['bellingham', 'ronaldo', 'zidane', 'vini', 'vinicius', 'mbappe', 'modric', 'kroos', 'كروس', 'مودريتش', 'ريال', 'مدريد'],
      'real': ['bellingham', 'ronaldo', 'zidane', 'vini', 'vinicius', 'mbappe', 'modric', 'kroos', 'ريال', 'مدريد', 'مريد'],
      'barcelona': ['messi', 'yamal', 'lamine', 'lewandowski', 'pedri', 'puyol', 'ronaldinho', 'برشلونه', 'برسا'],
      'barca': ['messi', 'yamal', 'lamine', 'lewandowski', 'pedri', 'برسا', 'برشلونه'],
      'liverpool': ['salah', 'nunez', 'ليفربول', 'ليفربول'],
      'city': ['haaland', 'de bruyne', 'سيتي', 'سيتي'],
      'united': ['ronaldo', 'garnacho', 'rashford', 'يونايتد', 'مانشستر'],
      'ahly': ['ahly', 'اهلي', 'الاهلي'],
      'zamalek': ['zamalek', 'زمالك', 'الزمالك', 'زيزو', 'شيكابالا'],

      // General Arabic
      'ريال': ['real', 'madrid', 'bellingham', 'ronaldo', 'mbappe', 'vini'],
      'مدريد': ['real', 'madrid', 'bellingham', 'ronaldo', 'mbappe', 'vini'],
      'برشلونه': ['barcelona', 'messi', 'yamal', 'lewandowski'],
      'برشا': ['barcelona', 'messi', 'yamal'],
      'الاهلي': ['ahly'],
      'الزمالك': ['zamalek'],
      'صلاح': ['salah'],
      'ميسي': ['messi'],
      'رونالدو': ['ronaldo', 'cr7'],
    };

    // Every token in search must find a match (either literally or via synonym)
    return searchTokens.every(token => {
      const equivalents = synonymMap[token] || [token];
      return equivalents.some(eq =>
        normalizedProductName.includes(eq) ||
        normalizedCategoryId.includes(eq)
      );
    });
  });

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (e) => {
    if ((e.key === 'Enter' || e.type === 'click') && search.trim()) {
      navigate(`/shop?search=${encodeURIComponent(search)}`);
    }
  };

  return (
    <div className="products-page" style={{ padding: '0 0 80px', background: 'var(--bg-main)' }}>
      <CategorySlider />

      <div className="shop-header" style={{
        textAlign: 'center',
        margin: '80px auto 40px',
        padding: '0 20px'
      }}>
        <h1 style={{
          fontFamily: "'Cinzel', serif",
          fontSize: 'clamp(28px, 4vw, 48px)',
          fontWeight: '400',
          color: '#ffffff',
          marginBottom: '10px',
          letterSpacing: '2px'
        }}>Gallery Collection</h1>
        <p style={{
          fontFamily: 'var(--font-nav)',
          fontSize: '11px',
          color: 'var(--accent-primary)',
          letterSpacing: '4px',
          textTransform: 'uppercase'
        }}>Find your perfect piece</p>
      </div>

      <motion.div
        className="search-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ maxWidth: '600px', margin: '0 auto 100px', padding: '0 20px' }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '100px',
          padding: '8px 25px',
          border: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease'
        }}
          className="search-bar-wrapper"
        >
          <FiSearch style={{ color: 'rgba(255,255,255,0.3)', fontSize: '20px' }} />
          <input
            id="shop-search"
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={handleSearch}
            placeholder="Search by artist, style or title..."
            style={{
              flex: 1,
              fontSize: '14px',
              padding: '12px 15px',
              background: 'transparent',
              border: 'none',
              color: '#fff',
              outline: 'none',
              fontFamily: 'var(--font-nav)',
              letterSpacing: '1px'
            }}
          />
        </div>
      </motion.div>

      <motion.div
        key={currentPage}
        className="posters-grid"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: {
              staggerChildren: 0.1
            }
          }
        }}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '40px',
          margin: '0 auto',
          padding: '0 30px'
        }}
      >
        {currentItems.length > 0 ? (
          currentItems.map((product) => (
            <ProductCard key={`${product.id}-${currentPage}`} product={product} />
          ))
        ) : (
          <p style={{ gridColumn: '1/-1', fontSize: '1.2em', color: '#666', margin: '40px auto', textAlign: 'center' }}>No results found.</p>
        )}
      </motion.div>

      {/* Ultra-Premium Navigation */}
      {totalPages > 1 && (
        <div className="pagination-container" style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '30px',
          marginTop: '100px',
          paddingBottom: '40px'
        }}>
          <div style={{
            fontFamily: 'var(--font-nav)',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '6px',
            color: 'rgba(255,255,255,0.25)',
            fontWeight: '600'
          }}>
            Explore Art Gallery
          </div>

          <div className="nav-group" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '30px'
          }}>
            <motion.button
              onClick={() => handlePageChange(currentPage - 1)}
              whileHover={{ x: -5, color: 'var(--accent-primary)' }}
              disabled={currentPage === 1}
              style={{
                background: 'transparent',
                border: 'none',
                color: currentPage === 1 ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.4)',
                cursor: currentPage === 1 ? 'default' : 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                transition: 'color 0.3s ease'
              }}
            >
              <FiArrowLeft />
            </motion.button>

            <div className="pagination-wrapper" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '12px',
              padding: '8px 20px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '100px',
              border: '1px solid rgba(255,255,255,0.06)',
              backdropFilter: 'blur(15px)',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}>
              {
                (() => {
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(i);
                  }

                  return pages.map((pageNum) => {
                    const isActive = currentPage === pageNum;
                    return (
                      <motion.div key={pageNum} style={{ position: 'relative' }}>
                        <motion.button
                          onClick={() => handlePageChange(pageNum)}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          style={{
                            width: '38px', height: '38px', borderRadius: '50%', border: 'none',
                            background: 'transparent', color: isActive ? '#000' : 'rgba(255,255,255,0.4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', fontFamily: 'var(--font-nav)', fontWeight: '700',
                            fontSize: '13px', zIndex: 2, position: 'relative',
                            transition: 'color 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
                          }}
                        >
                          {pageNum}
                        </motion.button>
                        {isActive && (
                          <motion.div
                            layoutId="active-pill"
                            style={{
                              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                              backgroundColor: 'var(--accent-primary)', borderRadius: '50%',
                              zIndex: 1, boxShadow: '0 0 25px rgba(197, 160, 89, 0.5)'
                            }}
                            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                          />
                        )}
                      </motion.div>
                    );
                  });
                })()
              }
            </div>

            <motion.button
              onClick={() => handlePageChange(currentPage + 1)}
              whileHover={{ x: 5, color: 'var(--accent-primary)' }}
              disabled={currentPage === totalPages}
              style={{
                background: 'transparent',
                border: 'none',
                color: currentPage === totalPages ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.4)',
                cursor: currentPage === totalPages ? 'default' : 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                transition: 'color 0.3s ease'
              }}
            >
              <FiArrowRight />
            </motion.button>
          </div>
        </div>
      )}

      <CustomArtCard />

      <style>{`
        @media (max-width: 1400px) {
            .posters-grid {
                grid-template-columns: repeat(3, 1fr) !important;
            }
        }
        @media (max-width: 1024px) {
            .posters-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 30px !important;
            }
        }
        @media (max-width: 768px) {
            .posters-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                gap: 20px !important;
                padding: 0 15px !important;
            }
        }
      `}</style>
    </div>
  );
};

export default Shop;
